import React, { useCallback, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  AddOutlined as AddIcon,
  LinkOff as LinkOffIcon,
  LinkOutlined as LinkIcon,
  ToggleOffOutlined as ToggleOffIcon,
  ToggleOnOutlined as ToggleOnIcon,
} from "@mui/icons-material";
import Section from "../../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import StatChip from "../../../common/display/StatChip";
import { colors, fonts } from "../../../../styles/theme";
import {
  ApplicationComponent,
  ApplicationComponentCategoryOption,
  ApplicationComponentTypeOption,
  ErwarteteDokumentart,
  Evidence,
} from "../../../../types";
import NoteIndicator from "../../../common/display/NoteIndicator";
import {
  ActionsMenuButton,
  ActionsMenuList,
} from "../../../common/overlays/ActionsMenu";

interface ApplicationComponentsSectionProps {
  components: ApplicationComponent[];
  componentTypeOptions: ApplicationComponentTypeOption[];
  componentCategoryOptions: ApplicationComponentCategoryOption[];
  evidencesByComponent: Record<string, Evidence[]>;
  canEdit: boolean;
  /** Aktuell im Nachweis-Abschnitt geöffneter Nachweis */
  selectedEvidenceId: string | null;
  onSelectEvidence: (evidenceId: string) => void;
  /**
   * Ergänzt einen Nachweis am Baustein-Typ; `anchor` verankert das Auswahlmenü. "zuordnen"
   * wählt einen vorhandenen Nachweis des Antrags, "anlegen" legt einen neuen an und ordnet ihn zu.
   * Ist der Baustein noch nicht im Antrag, nimmt die Seite ihn dabei auf.
   */
  onAddEvidence: (
    typeId: string,
    anchor: HTMLElement,
    modus: "zuordnen" | "anlegen",
  ) => void;
  /** Löst die Zuordnung zum Baustein-Typ der Zeile; der Nachweis bleibt am Antrag */
  onUnassignEvidence: (typeId: string, evidenceId: string) => void;
  /**
   * Aktiviert oder deaktiviert einen Baustein. Das Kennzeichen "vom Nutzer gesetzt" setzt der
   * Server selbst – hier wird nur der neue Zustand gesendet.
   */
  onToggleAktiv: (componentId: string, istAktiv: boolean) => Promise<void>;
  height: number;
}

type TreeLevel = "category" | "component" | "evidence";

interface TreeRow extends TableData {
  level: TreeLevel;
  /** Einrückungstiefe: 0 Kapitel, 1 Formular, 2 Unterformular, Nachweis eine tiefer */
  tiefe: number;
  /** Gliederungsnummer: Kapitel-Code bzw. ELiA-Nummer; beim Nachweis leer */
  nummer: string | null;
  label: string;
  /**
   * Zweite Zeile unter der Bezeichnung: beim Nachweis die Dokumentart, beim Antragsbaustein die
   * erwarteten Dokumentarten ("erwartet: Gutachten, Datenblatt").
   */
  art: string | null;
  /** Erwartete Dokumentarten des Baustein-Typs – reine Erwartung, keine Nachweiszeilen */
  erwartet: ErwarteteDokumentart[];
  /** Baustein-Typ der Zeile; beim Kapitel `null` */
  typeId: string | null;
  /** ID des Antragsbausteins – `null`, solange der Baustein nicht im Antrag ist */
  componentId: string | null;
  /** True, wenn der Baustein deaktiviert ist: Zeile wird optisch zurückgenommen */
  gedimmt: boolean;
  /** Weder an der Zeile noch darunter hängt ein Nachweis – steht grau */
  istLeer: boolean;
  expandable: boolean;
  expanded: boolean;
  evidence: Evidence | null;
}

/**
 * Eine einzige Spalte plus 96 px Aktionsspalte. Zusammen mit `fixedLayout` an der Tabelle ist
 * die Breite verbindlich: die Bezeichnung wächst nicht mit ihrem längsten Eintrag, sondern wird
 * abgeschnitten. Die 96 px der Aktionsspalte gehen von ihr ab – sonst wäre die Tabelle um genau
 * diese 96 px zu breit.
 *
 * Die Art steht nicht in einer eigenen Spalte, sondern als zweite Zeile unter der Bezeichnung:
 * am Nachweis ist sie dessen Dokumentart, am Baustein nur die Erwartung. Eine eigene Spalte
 * hätte für beides denselben Rang beansprucht und der Bezeichnung ihre Breite genommen.
 *
 * Weder Zustand noch Fortschritt haben eine Spalte. Aktiv ist der Normalfall und braucht keine
 * Auszeichnung, deaktiviert steht als Zusatz hinter der Bezeichnung. Der Bearbeitungsstand eines
 * Nachweises steht im Abschnitt daneben, der dafür die Breite hat; der Baum bleibt damit das,
 * was er ist – die ELiA-Gliederung.
 */
const columns: TableColumn[] = [
  {
    key: "label",
    label: "Bezeichnung",
    align: "left",
    width: "calc(100% - 96px)",
  },
];

/** Einrückung je Tiefe in Theme-Spacing-Einheiten */
const INDENT_JE_TIEFE = 2.5;

/**
 * ELiA-Nummer des Elternformulars: "16.1.3" → "16.1", "14.3b.1" → "14.3b". Ein Formular der
 * zweiten Ebene ("16.1") liefert die Kapitelnummer, die kein Baustein-Typ ist.
 */
const elternNummer = (nummer: string): string =>
  nummer.slice(0, Math.max(nummer.lastIndexOf("."), 0));

/** Kurzfassung der erwarteten Dokumentarten für die Zeile unter der Bezeichnung */
const erwartetText = (erwartet: ErwarteteDokumentart[]): string | null =>
  erwartet.length === 0
    ? null
    : `erwartet: ${erwartet.map((item) => item.evidence_type.name).join(", ")}`;

/**
 * Zweite Zeile unter der Bezeichnung: am Nachweis die Dokumentart, am Baustein die Erwartung.
 * Kleiner und zurückgenommen gesetzt, damit die Bezeichnung die Zeile führt.
 */
const renderArt = (row: TreeRow): React.ReactNode => {
  if (!row.art) return null;
  return (
    <Typography
      variant="caption"
      noWrap
      sx={{
        color: colors.text.secondary,
        display: "block",
        fontStyle: row.level === "component" ? "italic" : "normal",
      }}
    >
      {row.art}
    </Typography>
  );
};

/**
 * Antragsbausteine des ausgewählten Antrags als Baum entlang der ELiA-Gliederung:
 * Kapitel → Formular → Unterformular (wo ELiA eines hat) → Nachweis.
 *
 * Der Baum zeigt den ganzen Katalog, nicht nur die Bausteine im Antrag – die Gliederung ist fest,
 * an ihr liest auch die Behörde entlang. Grau steht, woran weder selbst noch darunter ein Nachweis
 * hängt; so sieht man zugeklappt, wo etwas drinsteckt. Ein Formular mit Unterformularen ist in
 * ELiA meist selbst ein Formular (Anlagen, Erläuterungstext, teils eigene Felder) und kann deshalb
 * wie jedes andere eigene Nachweise tragen. Grau ist es nur, wenn auch alle Unterformulare leer
 * sind.
 *
 * Aufgenommen wird ein Baustein mit seinem ersten Nachweis – das Zeilenmenü steht an jedem
 * Baustein, auch an denen, die noch nicht im Antrag sind.
 *
 * Der Baum wird bewusst als flache Zeilenliste an `BaseTable` übergeben: `renderExpandedRow`
 * kennt nur eine einzige offene Zeile (`expandedRowId`) und erlaubt keine Tabellenzeilen als
 * Kinder. Einrückung und Pfeil kommen deshalb aus `renderCell`.
 */
const ApplicationComponentsSection: React.FC<
  ApplicationComponentsSectionProps
> = ({
  components,
  componentTypeOptions,
  componentCategoryOptions,
  evidencesByComponent,
  canEdit,
  selectedEvidenceId,
  onSelectEvidence,
  onAddEvidence,
  onUnassignEvidence,
  onToggleAktiv,
  height,
}) => {
  /** Nur die vom Nutzer abweichend geschalteten Zeilen – der Rest folgt der Vorbelegung. */
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  /** Offenes Zeilenmenü eines Bausteins; `componentId` fehlt, solange er nicht im Antrag ist */
  const [zeilenMenue, setZeilenMenue] = useState<{
    typeId: string;
    componentId: string | null;
    istAktiv: boolean;
    anchor: HTMLElement;
  } | null>(null);

  const rows: TreeRow[] = useMemo(() => {
    const componentByType = new Map(
      components.map((component) => [
        component.application_component_type_id,
        component,
      ]),
    );
    const nummern = new Set(
      componentTypeOptions.flatMap((type) =>
        type.elia_nummer ? [type.elia_nummer] : [],
      ),
    );
    /** Kinder je Elternnummer; Formulare der zweiten Ebene hängen unter `kapitel:<ID>` */
    const kinder = new Map<string, ApplicationComponentTypeOption[]>();
    componentTypeOptions.forEach((type) => {
      const eltern =
        type.elia_nummer && nummern.has(elternNummer(type.elia_nummer))
          ? elternNummer(type.elia_nummer)
          : null;
      const schluessel = eltern ?? `kapitel:${type.category.id}`;
      kinder.set(schluessel, [...(kinder.get(schluessel) ?? []), type]);
    });
    const kinderVon = (type: ApplicationComponentTypeOption) =>
      (type.elia_nummer && kinder.get(type.elia_nummer)) || [];
    const nachweiseVon = (type: ApplicationComponentTypeOption) => {
      const component = componentByType.get(type.id);
      return component ? (evidencesByComponent[component.id] ?? []) : [];
    };
    const hatNachweis = (type: ApplicationComponentTypeOption): boolean =>
      nachweiseVon(type).length > 0 || kinderVon(type).some(hatNachweis);

    const result: TreeRow[] = [];

    const fuegeTypHinzu = (
      type: ApplicationComponentTypeOption,
      tiefe: number,
    ) => {
      const component = componentByType.get(type.id) ?? null;
      const evidences = nachweiseVon(type);
      const unterformulare = kinderVon(type);
      const expanded = toggled[type.id] ?? false;

      result.push({
        id: type.id,
        level: "component",
        tiefe,
        nummer: type.elia_nummer ?? null,
        label: type.name,
        art: component ? erwartetText(component.erwartete_dokumentarten) : null,
        erwartet: component?.erwartete_dokumentarten ?? [],
        typeId: type.id,
        componentId: component?.id ?? null,
        gedimmt: component ? !component.ist_aktiv : false,
        istLeer: !hatNachweis(type),
        expandable: evidences.length > 0 || unterformulare.length > 0,
        expanded,
        evidence: null,
      });

      if (!expanded) return;

      // Eigene Nachweise vor den Unterformularen: sie gehören zum Formular selbst, die
      // Unterformulare sind dessen nächste Gliederungsebene.
      evidences.forEach((evidence) => {
        result.push({
          id: `${type.id}:${evidence.id}`,
          level: "evidence",
          tiefe: tiefe + 1,
          nummer: null,
          label: evidence.bezeichnung || "Ohne Bezeichnung",
          art: evidence.evidence_type.name,
          erwartet: [],
          typeId: type.id,
          componentId: component?.id ?? null,
          gedimmt: component ? !component.ist_aktiv : false,
          istLeer: false,
          expandable: false,
          expanded: false,
          evidence,
        });
      });
      unterformulare.forEach((kind) => fuegeTypHinzu(kind, tiefe + 1));
    };

    componentCategoryOptions.forEach((category) => {
      const formulare = kinder.get(`kapitel:${category.id}`) ?? [];
      // Alles startet zugeklappt: Zu Beginn soll die ELiA-Gliederung als Ganzes sichtbar sein –
      // die 17 Oberkapitel auf einen Blick, statt dass die Bausteine des ersten gefüllten
      // Kapitels die übrigen aus der festen Höhe des Abschnitts schieben.
      const categoryExpanded = toggled[category.id] ?? false;

      result.push({
        id: category.id,
        level: "category",
        tiefe: 0,
        // ELiA-Kapitelnummer statt des internen Codes: darunter stehen die Formularnummern
        // ("1.1"), und zwei verschiedene Nummernsysteme untereinander lesen sich wie ein
        // Fehler. Mit "1" über "1.1" wird der Baum zur ELiA-Gliederung.
        nummer: category.elia_nummer ?? category.code,
        label: category.name,
        art: null,
        erwartet: [],
        typeId: null,
        componentId: null,
        gedimmt: false,
        istLeer: !formulare.some(hatNachweis),
        expandable: formulare.length > 0,
        expanded: categoryExpanded,
        evidence: null,
      });

      if (categoryExpanded) formulare.forEach((type) => fuegeTypHinzu(type, 1));
    });

    return result;
  }, [
    componentCategoryOptions,
    componentTypeOptions,
    components,
    evidencesByComponent,
    toggled,
  ]);

  const handleRowClick = useCallback(
    (row: TableData) => {
      const treeRow = row as TreeRow;
      if (treeRow.level === "evidence") {
        if (treeRow.evidence) onSelectEvidence(treeRow.evidence.id);
        return;
      }
      if (!treeRow.expandable) return;
      setToggled((prev) => ({ ...prev, [treeRow.id]: !treeRow.expanded }));
    },
    [onSelectEvidence],
  );

  const renderCell = useCallback(
    (column: TableColumn, row: TableData, value: any) => {
      const treeRow = row as TreeRow;
      const isEmpty = treeRow.istLeer;

      switch (column.key) {
        case "label":
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                pl: treeRow.tiefe * INDENT_JE_TIEFE,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 18,
                  flexShrink: 0,
                  color: colors.text.secondary,
                  display: "flex",
                }}
              >
                {treeRow.expandable &&
                  (treeRow.expanded ? (
                    <ArrowDownIcon fontSize="small" />
                  ) : (
                    <ArrowRightIcon fontSize="small" />
                  ))}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  {treeRow.nummer && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: fonts.monospace,
                        color: isEmpty
                          ? colors.text.disabled
                          : colors.text.secondary,
                        flexShrink: 0,
                      }}
                    >
                      {treeRow.nummer}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight:
                        treeRow.level === "evidence"
                          ? fonts.weights.regular
                          : fonts.weights.semiBold,
                      color: isEmpty
                        ? colors.text.disabled
                        : colors.text.primary,
                      fontStyle:
                        treeRow.level === "evidence" &&
                        !treeRow.evidence?.bezeichnung
                          ? "italic"
                          : "normal",
                    }}
                  >
                    {treeRow.label}
                  </Typography>
                  <NoteIndicator
                    note={treeRow.evidence?.note}
                    problem={treeRow.evidence?.problem}
                  />
                  {treeRow.level === "component" && treeRow.gedimmt && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.text.secondary, flexShrink: 0 }}
                    >
                      (deaktiviert)
                    </Typography>
                  )}
                </Box>
                {renderArt(treeRow)}
              </Box>
            </Box>
          );

        default:
          return <Typography variant="body2">{value}</Typography>;
      }
    },
    [canEdit],
  );

  const handleToggleAktiv = useCallback(async () => {
    const offen = zeilenMenue;
    setZeilenMenue(null);
    if (!offen?.componentId) return;
    await onToggleAktiv(offen.componentId, !offen.istAktiv);
  }, [zeilenMenue, onToggleAktiv]);

  /**
   * Öffnet die Nachweis-Auswahl aus dem Zeilenmenü heraus. Verankert bleibt sie an der
   * Schaltfläche des Menüs – die steht nach dem Schließen weiter an derselben Stelle.
   */
  const handleAddEvidenceAusMenue = useCallback(
    (modus: "zuordnen" | "anlegen") => {
      const offen = zeilenMenue;
      setZeilenMenue(null);
      if (!offen) return;
      // Aufklappen, damit der zugeordnete Nachweis gleich darunter zu sehen ist.
      setToggled((prev) => ({ ...prev, [offen.typeId]: true }));
      onAddEvidence(offen.typeId, offen.anchor, modus);
    },
    [zeilenMenue, onAddEvidence],
  );

  /**
   * Bausteine bekommen das Zeilenmenü, Nachweise das Lösen der Zuordnung, Kapitel nichts.
   *
   * Das Plus am Kapitel, über das ein Baustein in den Antrag kam, ist entfallen: alle Bausteine
   * stehen im Baum, und ein Baustein kommt mit seinem ersten Nachweis in den Antrag.
   */
  const renderActions = useCallback(
    (row: TableData) => {
      const treeRow = row as TreeRow;

      if (treeRow.level === "evidence") {
        const evidenceId = treeRow.evidence?.id;
        const typeId = treeRow.typeId;
        if (!evidenceId || !typeId) return null;
        return (
          <Tooltip title="Zuordnung lösen – der Nachweis bleibt am Antrag">
            <span>
              <IconButton
                size="small"
                disabled={!canEdit}
                aria-label={`Zuordnung von ${treeRow.label} lösen`}
                onClick={(event) => {
                  event.stopPropagation();
                  onUnassignEvidence(typeId, evidenceId);
                }}
                sx={{ color: colors.tableActions.expand.inactive }}
              >
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      }

      if (treeRow.level !== "component") return null;

      return (
        <ActionsMenuButton
          disabled={!canEdit}
          ariaLabel={`Aktionen zu ${treeRow.label}`}
          onOpen={(anchor) => {
            if (!treeRow.typeId) return;
            setZeilenMenue({
              typeId: treeRow.typeId,
              componentId: treeRow.componentId,
              istAktiv: !treeRow.gedimmt,
              anchor,
            });
          }}
        />
      );
    },
    [canEdit, onUnassignEvidence],
  );

  const getRowSx = useCallback((row: TableData) => {
    const treeRow = row as TreeRow;
    if (treeRow.level === "category") {
      return { backgroundColor: colors.background.tableHeaderAlt };
    }
    // Deaktivierte Bausteine bleiben stehen – die Behörde kann sie nachfordern –, treten aber
    // optisch zurück. Ihre Nachweise ebenso.
    if (treeRow.gedimmt) return { opacity: 0.55 };
    return {};
  }, []);

  return (
    <Section
      headerContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: fonts.weights.light, color: colors.text.primary }}
          >
            Antragsbausteine
          </Typography>
          <StatChip
            value={`${components.length}`}
            label={`von ${componentTypeOptions.length} Bausteinen im Antrag`}
            backgroundColor={colors.brand.teal50}
            valueColor={colors.brand.teal600}
          />
        </Box>
      }
      noPadding
      noOuterPadding
      sx={{ height }}
    >
      <BaseTable
        columns={columns}
        data={rows}
        renderCell={renderCell}
        renderActions={renderActions}
        actionsColumnWidth={96}
        actionsColumnAlign="right"
        getRowSx={getRowSx}
        onRowClick={handleRowClick}
        selectedRowId={
          rows.find((row) => row.evidence?.id === selectedEvidenceId)?.id
        }
        stickyHeader
        fixedLayout
        // Ohne Mindestbreite: die Kachel belegt nur 40 % der Seite und soll in keiner
        // Fensterbreite waagerecht scrollen. Die Spalten sind prozentual gesetzt, zu lange
        // Bezeichnungen enden mit Auslassungspunkten statt die Tabelle zu verbreitern.
        minWidth={0}
        maxHeight={height}
        disablePagination
        emptyMessage="Keine Kapitel hinterlegt"
      />

      <ActionsMenuList
        anchor={zeilenMenue?.anchor ?? null}
        onClose={() => setZeilenMenue(null)}
        items={[
          {
            label: "Nachweis zuordnen",
            icon: <LinkIcon fontSize="small" />,
            onClick: () => handleAddEvidenceAusMenue("zuordnen"),
            disabled: !zeilenMenue?.istAktiv,
          },
          {
            label: "Nachweis anlegen und zuordnen",
            icon: <AddIcon fontSize="small" />,
            onClick: () => handleAddEvidenceAusMenue("anlegen"),
            disabled: !zeilenMenue?.istAktiv,
          },
          // Aktivieren betrifft nur Bausteine im Antrag – die übrigen kommen über ihren ersten
          // Nachweis hinein.
          ...(zeilenMenue?.componentId
            ? [
                zeilenMenue.istAktiv
                  ? {
                      label: "Baustein deaktivieren",
                      icon: <ToggleOffIcon fontSize="small" />,
                      onClick: () => void handleToggleAktiv(),
                    }
                  : {
                      label: "Baustein aktivieren",
                      icon: <ToggleOnIcon fontSize="small" />,
                      onClick: () => void handleToggleAktiv(),
                    },
              ]
            : []),
        ]}
      />
    </Section>
  );
};

export default ApplicationComponentsSection;
